import {Modal} from 'antd'
import {getId} from 'apis/model/base'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useContext, useMemo, useState} from 'react'
import {bindQueryParam} from 'views/AppLocals/functions/routerHelper'
import {NullAsDefault} from 'views/Shared'
import EventContext from '../EventContext'
import {eventSchema} from "../../../../apis/schema/event";
import {useSelectEntities} from "../../../../redux/entities/useSelectEntities";
import {BiTrash} from "react-icons/bi";
import usePaginationHelper, {createPagerKey} from "../../hooks/usePaginationHelper";
import CRUD from "../../../Shared/enums/CRUD";
import {useHistory} from "../../../../modules/navigation/useRouter";
import {LayoutContext} from "../../../../components/layouts/Default/LayoutContext";
import {useLocation} from "react-use";
import {useRouteSearchParam} from "../../../../envs/_current/history";
import createContextMenu from "../../factory/createContextMenu";
import {RiSearchEyeLine} from "react-icons/ri";
import {showDeleteConfirmDialog} from "../../factory/createConfirmDialog";
import {DelayRender} from "../../../Shared/components/DelayRender";
import {event_checkin_getCheckinsByEvent_Api} from "../../../../apis/event";
import {renderEventCheckinItem} from "../functions/renderEventCheckinItem";
import {DetailViewForm} from "../ActionForms/EventCheckinForm";
import {useEventCheckinApi} from "../functions/useEventCheckinApi";
import LoadingBlock from "../../../Loading/LoadingBlock";

const EventSettingCheckins = ({
                                  filter_actions = {
                                      sortBy: 'sort-by',
                                      keyword: 'keyword',

                                  },
                              }) => {
    const {event: _event} = useContext(EventContext)
    const event = useSelectEntities(getId(_event), eventSchema)

    const t = useTranslate()
    const history = useHistory()
    const {isLg} = useContext(LayoutContext)
    const location = useLocation()

    const [refreshKey, setRefreshKey] = useState()

    const [action, setAction] = useState({type: undefined, value: undefined})

    const sortBy = useRouteSearchParam(filter_actions.sortBy)
    const keyword = useRouteSearchParam(filter_actions.keyword)

    const refresh_deps = useMemo(
        () => [
            refreshKey,
            `refreshToken=${_.get(location.state, 'refreshToken')}`,
        ],
        [refreshKey, location.state]
    )

    const onCancel = () => {
        setAction({})
    }

    const onSuccess = () => {
        onCancel()
        setRefreshKey(Date.now())
    }

    const {
        onRemoveCheckin
    } = useEventCheckinApi({
        notifyListeners: {
            onRemovedCheckin: onSuccess,
        },
    })

    const renderContextMenu = useCallback((item) => {
        return createContextMenu(
            [
                {
                    icon: <RiSearchEyeLine size={14} className="text-color-400"/>,
                    label: <span className="text-sm text-color-200">{t('view detail')}</span>,
                    key: CRUD.READ,
                },
                {
                    icon: <BiTrash size={14} className="text-primary-300"/>,
                    label: <span className="text-sm text-color-200">{t('delete')}</span>,
                    key: CRUD.DELETE,
                    invisible: !!!item?.edit
                },
            ].filter(e => item && (!!!e.invisible)),
            (key) => {
                switch (key) {
                    case CRUD.READ:
                        setAction({type: key, value: item})
                        break
                    case CRUD.DELETE:
                        const id = item?.id
                        if (id) {
                            showDeleteConfirmDialog({
                                translate: t,
                                title: 'delete this',
                                message: 'are you sure you want to delete this',
                                onOk: () => {
                                    onRemoveCheckin({}, {id,})
                                },
                            })
                        }
                        break
                    default:
                        break
                }
            }
        )
    }, [])

    const [pager_widget] = usePaginationHelper({
        SortByWidget: NullAsDefault,
        renderSortBy: NullAsDefault,
        isToggleDefault: true,
        AdvancedFilter: NullAsDefault,
        renderFilter: NullAsDefault,
        query_params: filter_actions,
        withKey: createPagerKey(refresh_deps),
        pagerInfo: {
            RenderEntity: React.Fragment,
            query: bindQueryParam({event_id: event?.id}),
            PaginationMeta: NullAsDefault,
            apiInfo: event_checkin_getCheckinsByEvent_Api,
            renderItem: renderEventCheckinItem({
                event: event,
                history: history,
                isLg,
                translate: t,
                renderContextMenu,
                handleAction: (type, value) => {
                    setAction({type, value});
                }
            }),
        },
        onInit: ({onSortChange = NullAsDefault, onFilterChange = NullAsDefault}) => {
            // onSortChange(sortBy)
            onFilterChange({})
        },
        filter_params: {},
        dependencies: [],
        renderHeader: ([searchBar, filterBar]) => (
            <div className="flex flex-col gap-4">
                {searchBar}
                {filterBar}
            </div>
        ),
    })

    const modal = useMemo(() => {
        let Content = NullAsDefault
        let Wrapper = ({children}) => children({initialValues: {}})
        let params = {}
        let modal_params = {}
        switch (action.type) {
            case CRUD.READ:
                modal_params = {
                    width: 600,
                    title: (
                        <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                            {t('event checkin information')}
                        </div>
                    ),
                }
                params = {
                    item: action?.value,
                    onCancel,
                }
                Content = ({...rest}) => (
                    <DetailViewForm
                        {...rest}
                        item={action?.value}
                    />
                )
                break
            default:
                break
        }

        return (
            <Modal
                footer={null}
                onCancel={onCancel}
                open={action.type}
                destroyOnHidden={true}
                className="custom-modal"
                {...modal_params}>
                <DelayRender lazy={true}>
                    <Wrapper>
                        {({isLoading, initialValues}) => {
                            if (!!isLoading) {
                                return <LoadingBlock/>
                            }
                            return (
                                <Content
                                    {...{
                                        onCancel,
                                        item: action.value,
                                    }}
                                    {...params}
                                    initialValues={initialValues}
                                />
                            )
                        }}
                    </Wrapper>
                </DelayRender>
            </Modal>
        )
    }, [action])

    return (
        <div className="mt-3 flex flex-col space-y-3">
            <div className="flex flex-col">
                <h2 className="font-bold text-xl text-gray-800 tracking-wide">
                    {t('check-in')}
                </h2>
                <div className="my-1 w-20 h-1 rounded-full bg-gray-400"/>
            </div>
            {pager_widget}
            {modal}
            <div className="h-12"/>
        </div>
    )
}

export default EventSettingCheckins
